import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import BoxedContent from 'ui/molecules/boxed-content';
import WideContent from 'core/layout/content/wide-content';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Translate from 'ui/atoms/translate';
import DashboardProductDetails from './dashboard-product-details';
import { InvestmentTokenForInvestor, InvestorPortfolio, WalletBalance } from 'api/models';
import { convertProductTypeFromApi } from 'core/api/conversions';
import InvestorInvestmentsTableView from '../../shared/investments-table-view';
import { useApiCall } from 'hooks/use-api-call';
import { PortfolioApi, ProductsApi, WalletsApi } from 'api/apis';
import LoadingRing from 'ui/atoms/loading-ring';
import Section from 'ui/atoms/section';
import Link from 'core/routing/link';
import WalletDetails from './wallet-details';
import Button from 'ui/atoms/button';
import AddWalletModal from './add-wallet-modal';
import Header from 'src/ui/atoms/header';

const InvestorProductDetailsPage: FunctionComponent<{ id: string }> = ({ id }) => {
  const { withApi, makeAuthenticatedApi, loading } = useApiCall();

  const [portfolio, setPortfolio] = useState<InvestorPortfolio>();
  const [token, setToken] = useState<InvestmentTokenForInvestor>();
  const [walletBalances, setWalletBalances] = useState<WalletBalance[]>();
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [hasAvailableWallets, setHasAvailableWallets] = useState(false);

  const portfolioApi: PortfolioApi = useMemo(() => makeAuthenticatedApi(PortfolioApi), [makeAuthenticatedApi]);

  const tokensApi: ProductsApi = useMemo(() => makeAuthenticatedApi(ProductsApi), [makeAuthenticatedApi]);

  const walletsApi: WalletsApi = useMemo(() => makeAuthenticatedApi(WalletsApi), [makeAuthenticatedApi]);

  const loadData = useCallback(() => {
    withApi(async () => {
      setPortfolio(await portfolioApi.portfolioRetrieve({ id }));
      setToken(await tokensApi.productsRetrieve({ id }));
      try {
        setWalletBalances(await walletsApi.walletsBalancesList({ token: id }));
      } catch {
        // no wallets associated
        setWalletBalances(undefined);
      }
      try {
        const res = await portfolioApi.portfolioAvailableProductsWalletsList({ productId: id });
        setHasAvailableWallets(res.length > 0);
      } catch {
        setHasAvailableWallets(false);
      }
    });
  }, [withApi, portfolioApi, tokensApi, walletsApi, id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // TODO(mara-cashlink): error handling

  if (!portfolio || loading || !token) return <LoadingRing />;

  const {
    lastSignedAt,
    productType,
    productName,
    totalInvestmentAmount,
    website,
    totalEarnings,
    nextProfit,
    lastProfit,
    runtime,
  } = portfolio;

  return (
    <>
      <Section>
        <WideContent>
          <BoxedContent title={productName} headerSize="large">
            <DashboardProductDetails
              productName={productName}
              productType={convertProductTypeFromApi(productType)}
              totalInvestmentAmount={totalInvestmentAmount}
              lastSignedAt={lastSignedAt}
              totalEarnings={totalEarnings}
              lastProfit={lastProfit}
              nextProfit={nextProfit}
              runtime={runtime}
              website={website}
            />
          </BoxedContent>
        </WideContent>
      </Section>

      <Section>
        <WideContent
          title={<Translate name="dashboardInvestorProductDetails.wallets" />}
          ignorePageTitle
          titleSpacing="medium"
          headerPanel={
            <Button
              variant="primary"
              size="small"
              icon="plus"
              iconColor="inherit"
              disabled={!hasAvailableWallets}
              onClick={() => setShowAddWallet(true)}
            >
              <Translate name="dashboardInvestorProductDetails.addWallet" />
            </Button>
          }
        >
          {(walletBalances || []).map((walletBalance) => {
            return <WalletDetails walletBalance={walletBalance} token={token} />;
          })}
        </WideContent>
      </Section>

      <Section>
        <WideContent>
          <>
            <Header size="small" spacing="medium">
              <Translate name="common.investments" />
            </Header>
            <InvestorInvestmentsTableView token={id} status="finished" />

            <Link icon="arrow-left" to="/dashboard/investorPortfolio">
              <Translate name="dashboardInvestorProductDetails.goToPortfolio" />
            </Link>
          </>
        </WideContent>
      </Section>

      {showAddWallet && (
        <AddWalletModal
          productId={id}
          productNetwork={token.tokenizationInfo?.networkType}
          onClose={() => setShowAddWallet(false)}
          onSuccess={loadData}
        />
      )}
    </>
  );
};

export default WithAuthenticatedPage(InvestorProductDetailsPage, AppType.INVESTOR);
